<template>
  <div
    class="bg-white transition-colors duration-150 ease-in-out"
    :class="[open ? 'group-hover:bg-blue-100/60' : 'group-hover:bg-gray-50']"
    v-bind="$attrs"
  >
    <!-- Trade Header Row -->
    <div
      class="flex items-center justify-between px-6 pt-8"
      @click="open = !open"
    >
      <div class="flex items-center space-x-3">
        <!-- Drag Handle -->
        <div
          v-if="!disableDraggable && projectsStore.project?.unarchived && !isEditingDisabled"
          class="drag-handle cursor-grab active:cursor-grabbing btn-icon btn-icon-default"
          @click.stop
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
        </div>

        <!-- Expand/Collapse Button -->
        <button
          data-test="open-trade"
          type="button"
          class="btn-icon btn-icon-default"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <!-- Add Trade Item Button -->
        <button
          v-if="projectsStore.project?.unarchived && !isEditingDisabled"
          type="button"
          class="btn-icon btn-icon-blue"
          @click.stop="createTradeItem()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
        </button>

        <button
          v-if="projectsStore.project?.unarchived && !isEditingDisabled"
          type="button"
          class="btn-icon btn-icon-blue"
          @click.stop="$emit('edit')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <button
          v-if="projectsStore.project?.unarchived && !isEditingDisabled"
          type="button"
          class="btn-icon btn-icon-red"
          @click.stop="$emit('delete')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <!-- Trade Info -->
        <div class="flex items-start space-x-2">
          <span class="font-semibold text-gray-900">{{ trade.billNumber }}</span>

          <span class="text-gray-700">{{ trade.name }}</span>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <!-- Trade Amount -->
        <div class="text-right text-sm font-medium text-gray-900">
          <div v-if="showWorksLabel">
            <span
              v-if="showAdjustmentsLabel || showProvisionalSumsLabel"
              class="text-gray-600"
            >Works:</span> {{ $tcc(totalTradeCost) }}
          </div>

          <div v-if="showProvisionalSumsLabel">
            <span
              v-if="showAdjustmentsLabel || showWorksLabel"
              class="text-gray-600"
            >Provisional Sums:</span> {{ $tcc(totalTradeProvisionalCost) }}
          </div>

          <div v-if="showAdjustmentsLabel">
            <span
              v-if="showWorksLabel || showProvisionalSumsLabel"
              class="text-gray-600"
            >Adjustments:</span> {{ $tcc(-(totalTradeCost + totalTradeProvisionalCost - totalTradeAdjustments)) }}
          </div>
        </div>

        <!-- Status Indicator -->
        <div
          v-if="totalTradeCost > 0"
          class="w-3.5 h-3.5 rounded-full"
          :class="assignedIndicator.color"
          :title="assignedIndicator.title"
        />

        <div
          v-else
          class="w-3.5 h-3.5 invisible"
        />
      </div>
    </div>

    <!-- Trade Items Grid -->
    <div
      v-if="open && tradeItems.length"
      class="border-t border-gray-200 bg-gray-50 mt-4"
    >
      <div class="overflow-x-auto">
        <div class="min-w-full grid grid-cols-[minmax(0,2fr)_auto_auto_auto_auto]">
          <!-- Grid Header -->
          <div class="contents bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wider child:border-b child:border-b-gray-200">
            <div class="px-6 py-3 text-left">
              {{ $t('HeadContract.BillOfQuantities.TradeRow.item') }}
            </div>

            <div class="px-6 py-3 text-left">
              {{ $t('HeadContract.BillOfQuantities.TradeRow.unit') }}
            </div>

            <div class="px-6 py-3 text-right">
              {{ $t('HeadContract.BillOfQuantities.TradeRow.quantity') }}
            </div>

            <div class="px-6 py-3 text-right">
              {{ $t('HeadContract.BillOfQuantities.TradeRow.rate') }}
            </div>

            <div class="px-6 py-3 text-right">
              {{ $t('HeadContract.BillOfQuantities.TradeRow.amount') }}
            </div>
          </div>

          <!-- Grid Items -->
          <Sortable
            :list="tradeItems"
            item-key="id"
            class="contents bg-white"
            :class="{ 'opacity-50 pointer-events-none': updateSortAction.is(States.LOADING) }"
            :disabled="!projectsStore.project?.unarchived || disableDraggable || isEditingDisabled"
            :options="{
              handle: '.drag-handle-trade-item-0'
            }"
            filter=".ignored"
            @end="changeSortOrder"
            @move.capture="handleMove"
          >
            <template
              v-if="updateSortAction.is(States.LOADING)"
              #header
            >
              <div class="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bottom-1/2">
                <AppSpinner />
              </div>
            </template>

            <template #item="{element}">
              <TradeRowItem
                :key="`${element.id}`"
                :ref="tradeItemRefs.set"
                :full-bill-item="props.trade.billNumber.toString()"
                :disable-draggable="disableDraggable"
                :trade-item="element"
                :project-unarchived="projectsStore.project?.unarchived"
                :is-editing-disabled="isEditingDisabled"
                :expand-to-depth="props.expandToDepth"
                :depth="0"
                @edit="editTradeItem($event?.tradeItem ?? element)"
                @delete="deleteTradeItem($event?.tradeItem ?? element)"
                @add="createTradeItem($event?.tradeItem ?? element)"
              />
            </template>
          </Sortable>
        </div>
      </div>
    </div>

    <!-- Empty Trade Items State -->
    <div
      v-else-if="open"
      class="border-y border-gray-200 px-6 py-8 text-center mt-4"
    >
      <div class="mx-auto h-8 w-8 text-gray-300 mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-8 h-8">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
      </div>

      <h3 class="text-sm font-medium text-gray-900 mb-1">
        {{ $t('HeadContract.BillOfQuantities.TradeRow.noTradeItemsFound') }}
      </h3>

      <p class="text-sm text-gray-500">
        {{ $t('HeadContract.BillOfQuantities.TradeRow.addFirstTradeItem') }}
      </p>
    </div>
  </div>

  <slot :open="open" />
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useUserStore } from '@/store/user';
import { useTemplateRefsList } from '@vueuse/core';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  disableDraggable: boolean;
  expandToDepth?: number;
}>();

const emits = defineEmits<{
  (e: 'edit');
  (e: 'delete');
  (e: 'modalOpen');
  (e: 'modalClose');
  (e: 'createTradeItem', data: { trade: Trade; tradeItem?: TradeItem });
  (e: 'editTradeItem', data: { tradeItem: TradeItem; trade: Trade });
}>();

const open = ref(false);
const tradeItemsStore = useTradeItemsStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const tradeItemRefs = useTemplateRefsList();

// Computed property to check if editing should be disabled for contract managers when BOQ is locked
const isEditingDisabled = computed(() => {
  return userStore.isRole(['contract_manager'], false) && projectsStore.project?.boq_locked;
});

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .with('package_trade_items', (query) => {
      query.with('package');
    })
    .where('trade_id', props.trade.id)
    .whereNull('parent_id')
    .orderBy('sort_order')
    .get();
});

const childTradeItems = computed(() => {
  return tradeItemsStore.models
    .with('package_trade_items')
    .where('trade_id', props.trade.id)
    .where('has_children', false)
    .where('budget', (budget) => budget > 0)
    .orderBy('sort_order')
    .get();
});

const totalTradeCost = computed(() => {
  return tradeItems.value.reduce((total, current) => {
    return total + current.childBudgetExcludingProvisional();
  }, 0);
});

const totalTradeAdjustments = computed(() => {
  return tradeItems.value.reduce((total, current) => {
    return total + current.childAdjustedBudget();
  }, 0);
});

const totalTradeProvisionalCost = computed(() => {
  return tradeItems.value.reduce((total, current) => {
    return total + current.childProvisionalBudget();
  }, 0);
});

const editTradeItem = (tradeItem: TradeItem) => {
  emits('editTradeItem', { tradeItem, trade: props.trade });
};

const createTradeItem = (tradeItem?: TradeItem) => {
  open.value = true;
  emits('createTradeItem', { trade: props.trade, tradeItem });
};

const deleteTradeItemAction = useStoreApiAction(tradeItemsStore.deleteTradeItem);

const deleteTradeItem = (tradeItem: TradeItem) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Trade Item?',
    text: `You are about to delete ${tradeItem.name}. This action cannot be undone.`,
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteTradeItemAction.request(tradeItem.id).then(() => {
        Swal.fire({
          icon: 'warning',
          title: 'Trade Item Deleted.',
        });
      }).catch((error) => {
        if(error?.status === 422) {
          Swal.fire({
            icon: 'error',
            title: 'Failed to delete trade item',
            text: error?.data.message,
          });
        }
      });
    }
  });
};

const updateSortAction = useStoreApiAction(tradeItemsStore.updateSort);

const changeSortOrderForTradeItem = (tradeItem: TradeItem, index: number) => {
  updateSortAction.request(tradeItem.id, {
    sort_order: index,
  });
};

const changeSortOrder = (e) => {
  const item = tradeItems.value.find((tradeItem) => tradeItem.id === parseInt(e.item.dataset.tradeItemId));

  changeSortOrderForTradeItem(item, e.newIndex + 1);
};

const handleMove = (e) => {
  if(e.related && e.related.classList.contains('ignored')) {
    return false;
  }
};

const tradeItemsAssignedToPackage = computed(() => {
  return childTradeItems.value.filter((tradeItem) => {
    return tradeItem.package_trade_items.length > 0;
  }).length;
});

const assignedIndicator = computed(() => {
  if(tradeItemsAssignedToPackage.value > 0 && tradeItemsAssignedToPackage.value < childTradeItems.value.length) {
    return {
      title: 'Some trade items have been assigned to a package',
      color: 'bg-orange-400',
    };
  } else if(tradeItemsAssignedToPackage.value === childTradeItems.value.length) {
    return {
      title: 'All trade items have been assigned to a package',
      color: 'bg-green-400',
    };
  }

  return {
    title: 'No trade items have been assigned to a package',
    color: 'bg-red-400',
  };
});

// Auto-open/close trade based on expandToDepth
watch(() => props.expandToDepth, (newDepth) => {
  if(newDepth !== undefined && tradeItems.value.length > 0) {
    open.value = true;
  } else if(newDepth === undefined) {
    open.value = false;
  }
}, { immediate: true });

const showWorksLabel = computed(() => {
  return totalTradeCost.value !== 0 || totalTradeProvisionalCost.value !== 0;
});

const showProvisionalSumsLabel = computed(() => {
  return totalTradeProvisionalCost.value !== 0;
});

const showAdjustmentsLabel = computed(() => {
  return (totalTradeCost.value + totalTradeProvisionalCost.value) - totalTradeAdjustments.value !== 0;
});

defineOptions({
  inheritAttrs: false,
});
</script>

<style scoped></style>
